import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';

const EventContext = createContext();

/** Path segments under /admin that are not event tags (tools, static pages). */
const RESERVED_ADMIN_PATH_SEGMENTS = new Set(['audience-match']);

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
};

export const EventProvider = ({ children }) => {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract event identifier from URL
  const getEventFromUrl = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'admin' && pathParts[2]) {
      const segment = pathParts[2];
      if (RESERVED_ADMIN_PATH_SEGMENTS.has(segment)) {
        return null;
      }
      return segment;
    }
    return null;
  };

  // Fetch all events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use different endpoints based on user role
      let response;
      if (user?.role === 'staff') {
        // Staff can only see their assigned events
        response = await api.get('/api/staff/assigned-events');
        setEvents(response.data.events || []);
      } else {
        // Admin can see all events
        response = await api.get('/api/events');
        setEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Fetch single event by ID or tag
  const fetchEvent = async (identifier) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use different endpoints based on user role
      let response;
      if (user?.role === 'staff') {
        // Staff use staff-specific endpoint
        response = await api.get(`/api/staff/event/${identifier}`);
        setCurrentEvent(response.data.event);
      } else {
        // Admin use admin endpoint
        response = await api.get(`/api/events/${identifier}`);
        setCurrentEvent(response.data.event);
      }
      return response.data.event;
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Switch to a different event
  const switchEvent = async (eventIdentifier) => {
    const event = await fetchEvent(eventIdentifier);
    if (event) {
      // Update URL to reflect the new event
      const currentPath = location.pathname;
      const pathParts = currentPath.split('/');
      
      if (pathParts[1] === 'admin') {
        // Replace the event part in the URL
        pathParts[2] = eventIdentifier;
        const newPath = pathParts.join('/');
        navigate(newPath);
      }
    }
  };

  // Initialize event context only when user is authenticated
  useEffect(() => {
    if (user) {
      fetchEvents();
    } else {
      // Clear events when user is not authenticated
      setEvents([]);
      setCurrentEvent(null);
    }
  }, [user]);

  // Update current event when URL changes (only if user is authenticated)
  useEffect(() => {
    if (!user) return;
    
    const eventIdentifier = getEventFromUrl();
    if (eventIdentifier) {
      fetchEvent(eventIdentifier);
    } else {
      setCurrentEvent(null);
    }
  }, [location.pathname, user]);

  // Get event by identifier
  const getEventByIdentifier = (identifier) => {
    return events.find(event => 
      event._id === identifier || 
      event.eventTag === identifier ||
      event.eventId === identifier
    );
  };

  // Check if user has access to an event
  const hasEventAccess = (event) => {
    // For now, all admins have access to all events
    // This can be extended with role-based permissions later
    return true;
  };

  // Get events that the user has access to
  const getAccessibleEvents = () => {
    return events.filter(hasEventAccess);
  };

  // Create new event
  const createEvent = async (eventData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/api/events', eventData);
      await fetchEvents(); // Refresh events list
      return response.data.event;
    } catch (error) {
      console.error('Error creating event:', error);
      setError(error.response?.data?.message || 'Failed to create event');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update event
  const updateEvent = async (eventId, eventData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put(`/api/events/${eventId}`, eventData);

      // Update current event if it's the one being updated (normalize id types from JSON)
      if (currentEvent && String(currentEvent._id) === String(eventId)) {
        setCurrentEvent(response.data.event);
      }
      
      await fetchEvents(); // Refresh events list
      return response.data.event;
    } catch (error) {
      console.error('Error updating event:', error);
      setError(error.response?.data?.message || 'Failed to update event');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete event
  const deleteEvent = async (eventId) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/api/events/${eventId}`);
      
      // Clear current event if it's the one being deleted
      if (currentEvent && currentEvent._id === eventId) {
        setCurrentEvent(null);
        navigate('/admin');
      }
      
      await fetchEvents(); // Refresh events list
    } catch (error) {
      console.error('Error deleting event:', error);
      setError(error.response?.data?.message || 'Failed to delete event');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // State
    currentEvent,
    events,
    loading,
    error,
    
    // Actions
    fetchEvents,
    fetchEvent,
    switchEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    getEventByIdentifier,
    getAccessibleEvents,
    hasEventAccess,
    
    // Utilities
    getEventFromUrl,
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
}; 